import { PaginationResponse } from "../../Shared/Pagination/PaginationResponse";
import { getApi } from "../../Shared/Services/RestService";
import {
  SmartAnalyticsActivityRow,
  SmartAnalyticsCandidateActivityRow,
  SmartAnalyticsCandidateFilters,
  SmartAnalyticsCandidateInvitationRow,
  SmartAnalyticsCandidateRow,
  SmartAnalyticsCompanyFilters,
  SmartAnalyticsCompanyOverview,
  SmartAnalyticsCompanyPayments,
  SmartAnalyticsCompanyRow,
  SmartAnalyticsDataServiceRow,
  SmartAnalyticsFeedbackRow,
  SmartAnalyticsInvitationRow,
  SmartAnalyticsJobTemplateRow,
  SmartAnalyticsRecruiterRow,
  SmartAnalyticsSearchRow,
} from "../Types/SmartAnalyticsType";

const BASE = "/admin/smart-analytics";

const toUuid = (id: string): string => {
  const hex = (id || "").replace(/-/g, "").trim();
  if (hex.length !== 32) return id;
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-");
};

const withParams = (params: Record<string, any>) => {
  const result: Record<string, any> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      result[key] = value;
    }
  });
  return result;
};

export const getSmartAnalyticsCandidates = (
  filters: SmartAnalyticsCandidateFilters = {},
  page: number = 0,
  size: number = 10
) =>
  getApi<PaginationResponse<SmartAnalyticsCandidateRow>>(
    `${BASE}/candidates`,
    withParams({ ...filters, page, size })
  );

export const getSmartAnalyticsCandidateActivities = (
  userId: string,
  search?: string,
  page: number = 0,
  size: number = 10
) =>
  getApi<PaginationResponse<SmartAnalyticsCandidateActivityRow>>(
    `${BASE}/candidates/${toUuid(userId)}/activities`,
    withParams({ search, page, size })
  );

export const getSmartAnalyticsCandidateInvitations = (
  userId: string,
  search?: string,
  status?: string,
  page: number = 0,
  size: number = 10
) =>
  getApi<PaginationResponse<SmartAnalyticsCandidateInvitationRow>>(
    `${BASE}/candidates/${toUuid(userId)}/invitations`,
    withParams({ search, status, page, size })
  );

export const getSmartAnalyticsCompanies = (
  filters: SmartAnalyticsCompanyFilters = {},
  page: number = 0,
  size: number = 10
) =>
  getApi<PaginationResponse<SmartAnalyticsCompanyRow>>(
    `${BASE}/companies`,
    withParams({ ...filters, page, size })
  );

export const getSmartAnalyticsCompanyOverview = (companyId: string) =>
  getApi<SmartAnalyticsCompanyOverview>(
    `${BASE}/companies/${toUuid(companyId)}/overview`
  );

export const getSmartAnalyticsCompanyRecruiters = (
  companyId: string,
  search?: string,
  filter?: string,
  page: number = 0,
  size: number = 12
) =>
  getApi<PaginationResponse<SmartAnalyticsRecruiterRow>>(
    `${BASE}/companies/${toUuid(companyId)}/recruiters`,
    withParams({ search, filter, page, size })
  );

export const getSmartAnalyticsCompanyPayments = (
  companyId: string,
  search?: string,
  status?: string,
  planType?: string,
  page: number = 0,
  size: number = 10
) =>
  getApi<SmartAnalyticsCompanyPayments>(
    `${BASE}/companies/${toUuid(companyId)}/payments`,
    withParams({ search, status, planType, page, size })
  );

export const getSmartAnalyticsCompanyJobTemplates = (
  companyId: string,
  search?: string,
  recruiterId?: string,
  jobStatus?: string,
  page: number = 0,
  size: number = 10
) =>
  getApi<PaginationResponse<SmartAnalyticsJobTemplateRow>>(
    `${BASE}/companies/${toUuid(companyId)}/job-templates`,
    withParams({ search, recruiterId, jobStatus, page, size })
  );

export const getSmartAnalyticsCompanyActivities = (
  companyId: string,
  search?: string,
  recruiterId?: string,
  page: number = 0,
  size: number = 10
) =>
  getApi<PaginationResponse<SmartAnalyticsActivityRow>>(
    `${BASE}/companies/${toUuid(companyId)}/activities`,
    withParams({ search, recruiterId, page, size })
  );

export const getSmartAnalyticsCompanySearches = (
  companyId: string,
  search?: string,
  recruiterId?: string,
  page: number = 0,
  size: number = 10
) =>
  getApi<PaginationResponse<SmartAnalyticsSearchRow>>(
    `${BASE}/companies/${toUuid(companyId)}/searches`,
    withParams({ search, recruiterId, page, size })
  );

export const getSmartAnalyticsCompanyInvitations = (
  companyId: string,
  search?: string,
  recruiterId?: string,
  status?: string,
  page: number = 0,
  size: number = 10
) =>
  getApi<PaginationResponse<SmartAnalyticsInvitationRow>>(
    `${BASE}/companies/${toUuid(companyId)}/invitations`,
    withParams({ search, recruiterId, status, page, size })
  );

export const getSmartAnalyticsCompanyDataService = (
  companyId: string,
  recruiterId?: string,
  page: number = 0,
  size: number = 10
) =>
  getApi<PaginationResponse<SmartAnalyticsDataServiceRow>>(
    `${BASE}/companies/${toUuid(companyId)}/data-service`,
    withParams({ recruiterId, page, size })
  );

export const getSmartAnalyticsCompanyFeedbacks = (
  companyId: string,
  search?: string,
  recruiterId?: string,
  status?: string,
  page: number = 0,
  size: number = 10
) =>
  getApi<PaginationResponse<SmartAnalyticsFeedbackRow>>(
    `${BASE}/companies/${toUuid(companyId)}/feedbacks`,
    withParams({ search, recruiterId, status, page, size })
  );
